/**
 * zod validation for the Snapshot. Mirrors src/types.ts exactly — the two are
 * pinned together by the assignability assertions at the bottom of this file,
 * so drift is a typecheck failure rather than a runtime surprise.
 *
 * Date/time formats are validated with plain regexes rather than zod's own
 * datetime helpers, which have moved between zod majors.
 */
import { z } from "zod";

import { SNAPSHOT_SCHEMA } from "./types.js";
import type { Snapshot } from "./types.js";

/** YYYY-MM-DD */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "expected a YYYY-MM-DD date");

/** ISO 8601 UTC, e.g. 2026-08-08T04:12:00.000Z */
const isoUtcDateTime = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/,
    "expected an ISO 8601 UTC timestamp ending in Z",
  );

/** Amounts and quantities. -ve = debit, +ve = credit. */
const amount = z.number();

const companySchema = z.object({
  name: z.string().min(1),
  financialYearFrom: isoDate,
  booksFrom: isoDate,
  baseCurrency: z.string(),
  gstin: z.string().optional(),
  state: z.string().optional(),
});

const groupSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  parent: z.string().nullable(),
  primaryGroup: z.string(),
  isRevenue: z.boolean(),
  isDeemedPositive: z.boolean(),
  affectsGrossProfit: z.boolean(),
});

const ledgerContactSchema = z.object({
  phone: z.string().optional(),
  mobile: z.string().optional(),
  email: z.string().optional(),
});

const ledgerSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  parent: z.string(),
  openingBalance: amount,
  closingBalance: amount,
  isBillWiseOn: z.boolean(),
  isCostCentresOn: z.boolean(),
  creditPeriodDays: z.number().int().optional(),
  contact: ledgerContactSchema.optional(),
  address: z.array(z.string()).optional(),
  state: z.string().optional(),
  pincode: z.string().optional(),
  gstin: z.string().optional(),
  gstRegistrationType: z.string().optional(),
});

const voucherTypeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  parent: z.string(),
  affectsStock: z.boolean(),
  isDeemedPositive: z.boolean(),
  numberingMethod: z.string().optional(),
});

const costCategorySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  allocateRevenue: z.boolean(),
  allocateNonRevenue: z.boolean(),
});

const costCentreSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  parent: z.string().nullable(),
  category: z.string(),
});

const unitSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  isSimple: z.boolean(),
  decimalPlaces: z.number().int().min(0),
  baseUnit: z.string().optional(),
  additionalUnit: z.string().optional(),
  conversion: z.number().optional(),
});

const godownSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  parent: z.string().nullable(),
});

const stockGroupSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  parent: z.string().nullable(),
});

const stockItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  parent: z.string(),
  category: z.string().optional(),
  baseUnit: z.string(),
  openingQty: z.number(),
  openingValue: amount,
  closingQty: z.number(),
  closingValue: amount,
  hsnCode: z.string().optional(),
  gstRate: z.number().optional(),
});

export const billTypeSchema = z.enum([
  "New Ref",
  "Agst Ref",
  "Advance",
  "On Account",
]);

const billAllocationSchema = z.object({
  billName: z.string(),
  billType: billTypeSchema,
  amount,
  dueDate: isoDate.optional(),
});

const costAllocationSchema = z.object({
  costCentre: z.string(),
  category: z.string().optional(),
  amount,
});

const bankAllocationSchema = z.object({
  instrumentNumber: z.string().optional(),
  instrumentDate: isoDate.optional(),
  bankDate: isoDate.optional(),
  transactionType: z.string().optional(),
});

const ledgerEntrySchema = z.object({
  ledger: z.string(),
  amount,
  isDeemedPositive: z.boolean(),
  billAllocations: z.array(billAllocationSchema),
  costAllocations: z.array(costAllocationSchema),
  bankAllocation: bankAllocationSchema.optional(),
});

const inventoryEntrySchema = z.object({
  stockItem: z.string(),
  qty: z.number(),
  rate: z.number(),
  amount,
  unit: z.string(),
  godown: z.string().optional(),
  batch: z.string().optional(),
  expiryDate: isoDate.optional(),
  ledgerAllocations: z.array(
    z.object({ ledger: z.string(), amount }),
  ),
});

const voucherSchema = z.object({
  id: z.string().min(1),
  date: isoDate,
  voucherType: z.string(),
  voucherNumber: z.string(),
  reference: z.string().optional(),
  referenceDate: isoDate.optional(),
  narration: z.string().optional(),
  partyLedger: z.string().optional(),
  isCancelled: z.boolean(),
  isOptional: z.boolean(),
  ledgerEntries: z.array(ledgerEntrySchema),
  inventoryEntries: z.array(inventoryEntrySchema),
});

export const snapshotSchema = z.object({
  schema: z.literal(SNAPSHOT_SCHEMA),
  companyId: z.string().min(1),
  generatedAt: isoUtcDateTime,
  lastAlterIdMaster: z.number().int(),
  lastAlterIdVoucher: z.number().int(),

  /** present only on a role-redacted copy; see types.ts */
  role: z.object({ id: z.string(), label: z.string() }).optional(),

  company: companySchema,

  groups: z.array(groupSchema),
  ledgers: z.array(ledgerSchema),
  voucherTypes: z.array(voucherTypeSchema),
  costCategories: z.array(costCategorySchema),
  costCentres: z.array(costCentreSchema),
  units: z.array(unitSchema),
  godowns: z.array(godownSchema),
  stockGroups: z.array(stockGroupSchema),
  stockItems: z.array(stockItemSchema),
  vouchers: z.array(voucherSchema),
});

export const snapshotMetaSchema = z.object({
  hash: z.string().regex(/^[0-9a-f]{64}$/, "expected a SHA-256 hex digest"),
  generatedAt: isoUtcDateTime,
  sizeBytes: z.number().int().nonnegative(),
  schema: z.number().int().positive(),
});

export const companyMetaSchema = snapshotMetaSchema.extend({
  companyId: z.string().min(1),
});

/** Throws a ZodError describing every problem. */
export function parseSnapshot(value: unknown): Snapshot {
  return snapshotSchema.parse(value) as Snapshot;
}

export function safeParseSnapshot(value: unknown) {
  return snapshotSchema.safeParse(value);
}

/* -------------------------------------------------------------------------
 * Drift guard: types.ts and this schema must describe the same shape.
 * Either assertion failing is a typecheck error, not a runtime one.
 * ---------------------------------------------------------------------- */
type Assert<T extends true> = T;
type Extends<A, B> = A extends B ? true : false;

// exported only so `noUnusedLocals` does not strip them from the typecheck
export type SchemaMatchesType = Assert<
  Extends<z.infer<typeof snapshotSchema>, Snapshot>
>;
export type TypeMatchesSchema = Assert<
  Extends<Snapshot, z.infer<typeof snapshotSchema>>
>;
